import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, DollarSign, ShieldCheck, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Seo from "@/components/Seo";
import FaqSection, { faqJsonLd } from "@/components/FaqSection";
import { getStateBySlug, formatMoney } from "@/data/states";

function buildFaq(state) {
  const cities = state.cities.slice(0, 3).join(", ");
  return [
    {
      q: `How much does assisted living cost in ${state.name}?`,
      a: `Assisted living in ${state.name} averages around ${formatMoney(state.cost.assisted)} per month in 2025. Costs typically range from ${formatMoney(state.cost.assisted * 0.75)} to ${formatMoney(state.cost.assisted * 1.4)} depending on city, level of care, and amenities. Memory care runs higher (around ${formatMoney(state.cost.memory)}/mo) while independent living is lower (around ${formatMoney(state.cost.independent)}/mo).`,
    },
    {
      q: `Does Medicaid pay for assisted living in ${state.name}?`,
      a: state.medicaidWaiver ? `${state.medicaidWaiver} Medicaid does NOT cover room and board, but the state can help with personal care services for income-qualifying residents. Long waitlists are common — apply as soon as you suspect care will be needed.` : `${state.name} has limited Medicaid coverage for assisted living. Medicaid does not cover room and board; check with your local Area Agency on Aging for state-specific options.`,
    },
    {
      q: `What are the best cities in ${state.name} for senior living?`,
      a: `In ${state.name}, families most often choose communities in ${cities}${state.cities.length > 3 ? ", and other major metros" : ""}. Larger metros offer the deepest selection of communities and care levels; smaller cities can offer warmer, more personalized environments at often lower cost.`,
    },
    {
      q: `What's the difference between assisted living and memory care in ${state.name}?`,
      a: `Assisted living in ${state.name} supports daily tasks (bathing, dressing, medications) while preserving independence. Memory care is specifically designed for residents with Alzheimer's or dementia — secured environments, specially trained staff, and structured routines. Memory care typically costs ${formatMoney(state.cost.memory - state.cost.assisted)}/month more than assisted living in ${state.name}.`,
    },
    {
      q: `How do I choose a senior living community in ${state.name}?`,
      a: `Start with our free 3-minute quiz to identify the right level of care. Then tour 2-3 communities, asking about staff-to-resident ratio, staff turnover, dining quality, activities, and cost transparency. Check ${state.abbr} state inspection reports — they're public. Talk to residents and families when you tour. ${state.note}`,
    },
    {
      q: `What are veterans' benefits for senior care in ${state.name}?`,
      a: `Veterans and surviving spouses in ${state.name} may qualify for VA Aid & Attendance — an additional $1,500–$2,800/month on top of base pension to help cover assisted living or in-home care. Apply through your local VA office or a VA-accredited agent. Most senior living communities in ${state.name} are familiar with the program and can help you apply.`,
    },
  ];
}

export default function StateDetail() {
  const { state: slug } = useParams();
  const state = getStateBySlug(slug);
  if (!state) return <Navigate to="/senior-care" replace />;

  const faq = buildFaq(state);
  const url = `https://carehomeconnect.org/senior-care/${state.slug}`;

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid={`state-detail-${state.slug}`}>
      <Seo
        title={`Senior care in ${state.name} — costs, options & local guidance`}
        description={`Average senior care costs in ${state.name}: assisted living ${formatMoney(state.cost.assisted)}/mo, memory care ${formatMoney(state.cost.memory)}/mo. Top cities, Medicaid options, and a free 3-minute quiz to match you with the right care.`}
        path={`/senior-care/${state.slug}`}
        keywords={`assisted living ${state.name}, memory care ${state.name}, senior living ${state.name}, ${state.name} senior care cost, senior care ${state.cities[0]}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": `Senior care in ${state.name}`,
            "url": url,
            "description": `Senior care guidance, costs, and options for families in ${state.name}.`,
            "about": {
              "@type": "Place",
              "name": state.name,
              "address": { "@type": "PostalAddress", "addressRegion": state.abbr, "addressCountry": "US" },
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://carehomeconnect.org/" },
                { "@type": "ListItem", "position": 2, "name": "By state", "item": "https://carehomeconnect.org/senior-care" },
                { "@type": "ListItem", "position": 3, "name": state.name, "item": url },
              ],
            },
          },
          faqJsonLd(faq),
        ]}
      />
      <PublicNav />

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-10">
        <Link to="/senior-care" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2C4C3B] mb-6">
          <ArrowLeft className="w-4 h-4" /> All states
        </Link>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#9CAF88]/15 text-[#2C4C3B] text-xs font-medium mb-5">
          <MapPin className="w-3.5 h-3.5" /> {state.region} · {state.abbr}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A2E24] tracking-tight leading-[1.05]">
          Senior care in <span className="text-[#9CAF88]">{state.name}</span>
        </h1>
        <p className="mt-6 text-lg text-[#5C6B62] leading-relaxed max-w-2xl">
          {state.note} Below: average costs, popular metros, Medicaid notes, and the questions families in {state.name} ask us most.
        </p>
        <div className="mt-8">
          <Link to="/quiz" data-testid={`state-${state.slug}-quiz-cta`}>
            <Button className="bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-14 px-8 text-base font-medium">
              Find the best care option <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Cost grid */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-12">
        <div className="text-sm text-[#9CAF88] font-semibold mb-3 uppercase tracking-wider">Average monthly cost · 2025</div>
        <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24] mb-6">What senior care costs in {state.name}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Independent Living", price: state.cost.independent, sub: "55+ active community" },
            { label: "Assisted Living", price: state.cost.assisted, sub: "Help with daily tasks" },
            { label: "Memory Care", price: state.cost.memory, sub: "Secured, dementia-trained" },
            { label: "In-Home Care", price: state.cost.inHome, sub: "44hrs/wk, ~$28/hr" },
          ].map((c) => (
            <div key={c.label} className="card-soft p-5">
              <div className="text-xs text-[#5C6B62] uppercase tracking-wider mb-2">{c.label}</div>
              <div className="font-display text-3xl font-semibold text-[#1A2E24]">{formatMoney(c.price)}</div>
              <div className="text-xs text-[#5C6B62] mt-1">{c.sub}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#5C6B62] mt-4 leading-relaxed">
          Cost figures are 2025 statewide averages compiled from public industry surveys (Genworth, A Place for Mom). Actual pricing varies by city, care level, and amenities. Get a community-specific quote in your zip with our free quiz.
        </p>
      </section>

      {/* Top cities */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-12">
        <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24] mb-6">Popular metros for senior living in {state.name}</h2>
        <div className="flex flex-wrap gap-2.5">
          {state.cities.map((city) => (
            <Link
              key={city}
              to="/quiz"
              data-testid={`state-${state.slug}-city-${city.toLowerCase().replace(/\s+/g, "-")}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E2DFD8] text-[#1A2E24] hover:border-[#9CAF88] hover:text-[#2C4C3B] transition text-sm font-medium"
            >
              <MapPin className="w-3.5 h-3.5 text-[#9CAF88]" /> {city}
            </Link>
          ))}
        </div>
      </section>

      {/* Medicaid */}
      {state.medicaidWaiver && (
        <section className="max-w-5xl mx-auto px-6 md:px-12 pb-12">
          <div className="card-soft p-6 md:p-10 flex flex-col md:flex-row gap-6">
            <div className="w-12 h-12 rounded-xl bg-[#9CAF88]/15 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-6 h-6 text-[#2C4C3B]" />
            </div>
            <div>
              <h2 className="font-display text-xl md:text-2xl font-semibold text-[#1A2E24] mb-3">Medicaid &amp; financial help in {state.name}</h2>
              <p className="text-[#1A2E24] leading-relaxed">{state.medicaidWaiver}</p>
              <p className="text-[#5C6B62] text-sm leading-relaxed mt-4">
                Medicaid does <strong>not</strong> cover room-and-board costs. For private-pay families, long-term care insurance, VA Aid &amp; Attendance, and home equity (via reverse mortgage or sale) are the most common funding sources. Your matched advisor can help you build a funding plan.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* What we look for */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-12">
        <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24] mb-6">How we vet communities in {state.name}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: ShieldCheck, t: "Inspection record", b: `Clean ${state.abbr} state inspection history with no recent serious deficiencies.` },
            { icon: Heart, t: "Staff-to-resident ratio", b: "We only refer communities that disclose their ratios — and meet the level your loved one needs." },
            { icon: DollarSign, t: "Pricing transparency", b: "All-inclusive pricing or clear care-tier menus — no surprise bills." },
          ].map((x, i) => (
            <div key={i} className="card-soft p-5">
              <div className="w-10 h-10 rounded-xl bg-[#9CAF88]/15 flex items-center justify-center mb-3">
                <x.icon className="w-5 h-5 text-[#2C4C3B]" />
              </div>
              <h3 className="font-display font-semibold text-[#1A2E24] mb-1.5">{x.t}</h3>
              <p className="text-sm text-[#5C6B62] leading-relaxed">{x.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <FaqSection
        eyebrow={`${state.name} families ask`}
        title={`Senior care in ${state.name} — your questions answered`}
        items={faq}
        testIdPrefix={`state-${state.slug}-faq`}
      />

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-16">
        <div className="bg-[#2C4C3B] rounded-3xl p-10 md:p-16 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white max-w-2xl mx-auto leading-tight">
            Find the right care in {state.name} — in 3 minutes
          </h2>
          <p className="mt-5 text-[#F7F5F0]/80 max-w-lg mx-auto">
            Free, gentle, and matched to your loved one's needs and budget. No sales pressure, no spam.
          </p>
          <Link to="/quiz" data-testid={`state-${state.slug}-final-cta`} className="inline-block mt-8">
            <span className="inline-flex items-center gap-2 bg-[#E07A5F] hover:bg-[#C96B52] text-white rounded-xl h-14 px-8 text-base font-medium">
              Start the quiz <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
